/**
 * InsightPro™ — Stock & Inventory Page  v1.0
 *
 * Drill-down hierarchy:
 *   Overview → Department Detail → Product Detail
 *   Overview → Branch/Store Detail → Product Detail
 *   (Product Detail accessible from both paths)
 *
 * Mobile-first: all grids stack on mobile, touch targets 44px+,
 * no horizontal overflow, readable at 12px+.
 */
import React, { useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { pbiQuery } from '../utils/api';

function getDateRange(period) {
  const now   = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const y = today.getFullYear(), m = today.getMonth()+1, d = today.getDate();
  const dow = today.getDay();
  let startDate, endDate;
  if (period === 'TODAY') {
    startDate=`${y},${m},${d}`; endDate=`${y},${m},${d}`;
  } else if (period === 'YESTERDAY' || period === 'YEST') {
    const yest = new Date(today); yest.setDate(yest.getDate()-1);
    const yy=yest.getFullYear(),ym=yest.getMonth()+1,yd=yest.getDate();
    startDate=`${yy},${ym},${yd}`; endDate=`${yy},${ym},${yd}`;
  } else if (period === 'WTD') {
    const s=new Date(today); s.setDate(today.getDate()-(dow===0?6:dow-1));
    startDate=`${s.getFullYear()},${s.getMonth()+1},${s.getDate()}`; endDate=`${y},${m},${d}`;
  } else if (period === 'MTD') {
    startDate=`${y},${m},1`; endDate=`${y},${m},${d}`;
  } else if (period === 'QTD') {
    startDate=`${y},${Math.floor((m-1)/3)*3+1},1`; endDate=`${y},${m},${d}`;
  } else if (period === 'YTD') {
    startDate=`${y},1,1`; endDate=`${y},${m},${d}`;
  } else {
    startDate=`${y},${m},1`; endDate=`${y},${m},${d}`;
  }
  return { startDate, endDate };
}
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

// ── Constants ─────────────────────────────────────────────────
const GOLD  = '#c4922a';
const GREEN = '#1d9e75';
const RED   = '#c0392b';
const AMBER = '#d4a017';

// ── Responsive hook ───────────────────────────────────────────
function useIsMobile() {
  const [v, set] = React.useState(() => window.innerWidth < 768);
  React.useEffect(() => {
    const fn = () => set(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return v;
}

// ── Data hook ─────────────────────────────────────────────────
function usePbi(dax) {
  const [data,    setData]    = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error,   setError]   = React.useState(null);
  const load = useCallback(async () => {
    if (!dax) return;
    setLoading(true); setError(null);
    try {
      const r = await pbiQuery({ dax });
      setData(r?.rows || []);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, [dax]); // eslint-disable-line
  React.useEffect(() => { load(); }, [load]);
  return { data, loading, error, reload: load };
}

// ── Formatters ────────────────────────────────────────────────
const fmtR = n => {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000) return `R ${(v/1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000)     return `R ${(v/1_000).toFixed(0)}K`;
  return `R ${v.toFixed(0)}`;
};
const fmtN  = n => n == null ? '—' : Number(n).toLocaleString('en-ZA');
const fmtPct= n => n == null ? '—' : `${(Number(n)*100).toFixed(1)}%`;

// ── Shared UI ─────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'48px 0' }}>
      <div style={{ width:28, height:28, border:`3px solid rgba(196,146,42,0.2)`, borderTop:`3px solid ${GOLD}`, borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function ErrorBox({ msg, onRetry }) {
  return (
    <div style={{ padding:'24px', textAlign:'center' }}>
      <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:RED, letterSpacing:'0.12em', marginBottom:8 }}>DATA LOAD ERROR</div>
      <div style={{ fontSize:13, color:'var(--page-muted)', marginBottom:16 }}>{msg}</div>
      {onRetry && <button onClick={onRetry} style={{ fontFamily:'var(--font-mono)', fontSize:10, padding:'7px 16px', background:'none', border:`1px solid ${GOLD}`, color:GOLD, cursor:'pointer', borderRadius:2 }}>RETRY</button>}
    </div>
  );
}

function Card({ children, style }) {
  return <div style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:4, overflow:'hidden', ...style }}>{children}</div>;
}

function CardHeader({ title, subtitle }) {
  return (
    <div style={{ padding:'14px 16px 12px', borderBottom:'1px solid var(--card-border)' }}>
      <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--page-muted)' }}>{title}</div>
      {subtitle && <div style={{ fontSize:11, color:'var(--page-muted)', marginTop:3 }}>{subtitle}</div>}
    </div>
  );
}

function KpiCard({ label, value, sub, subColor, alert }) {
  return (
    <div style={{
      background: alert ? 'rgba(192,57,43,0.04)' : 'var(--card)',
      border: `1px solid ${alert ? 'rgba(192,57,43,0.35)' : 'var(--card-border)'}`,
      borderRadius:4, padding:'16px 16px 14px',
    }}>
      <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--page-muted)', marginBottom:8 }}>{label}</div>
      <div style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:700, color: alert ? RED : 'var(--page-text)', lineHeight:1, marginBottom:6, wordBreak:'break-word' }}>{value}</div>
      {sub && <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color: subColor || 'var(--page-muted)' }}>{sub}</div>}
    </div>
  );
}

function Breadcrumb({ crumbs, onNavigate }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:20, flexWrap:'wrap' }}>
      {crumbs.map((c, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span style={{ color:'var(--page-muted)', fontSize:12 }}>›</span>}
          <span onClick={() => i < crumbs.length-1 && onNavigate(i)}
            style={{ fontFamily:'var(--font-mono)', fontSize:10, letterSpacing:'0.1em', textTransform:'uppercase',
              color: i === crumbs.length-1 ? GOLD : 'var(--page-muted)',
              cursor: i < crumbs.length-1 ? 'pointer' : 'default',
              textDecoration: i < crumbs.length-1 ? 'underline' : 'none' }}>
            {c.label}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

function RowItem({ rank, label, sub, value, value2, value2Color, bar, barMax, onClick }) {
  const barW = barMax > 0 ? Math.min(100, (bar / barMax) * 100) : 0;
  return (
    <div onClick={onClick} style={{
      display:'flex', alignItems:'center', gap:8, padding:'12px 12px',
      borderBottom:'1px solid var(--card-border)',
      cursor: onClick ? 'pointer' : 'default',
      transition:'background 0.12s', minWidth:0, overflow:'hidden',
      minHeight: 44,
    }}
      onMouseEnter={e => onClick && (e.currentTarget.style.background='var(--page)')}
      onMouseLeave={e => onClick && (e.currentTarget.style.background='')}
    >
      {rank != null && <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)', width:18, flexShrink:0 }}>{String(rank+1).padStart(2,'0')}</div>}
      <div style={{ flex:1, minWidth:0, overflow:'hidden' }}>
        <div style={{ fontSize:13, color:'var(--page-text)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{label}</div>
        {sub && <div style={{ fontSize:11, color:'var(--page-muted)', marginTop:2 }}>{sub}</div>}
        {barMax != null && (
          <div style={{ height:3, background:'var(--card-border)', borderRadius:2, marginTop:4 }}>
            <div style={{ height:3, width:`${barW}%`, background:GOLD, borderRadius:2 }}/>
          </div>
        )}
      </div>
      <div style={{ textAlign:'right', flexShrink:0 }}>
        <div style={{ fontFamily:'var(--font-display)', fontSize:13, fontWeight:600, color:'var(--page-text)', whiteSpace:'nowrap' }}>{value}</div>
        {value2 != null && <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color: value2Color || 'var(--page-muted)', whiteSpace:'nowrap' }}>{value2}</div>}
      </div>
      {onClick && <div style={{ color:'var(--page-muted)', fontSize:12, flexShrink:0 }}>›</div>}
    </div>
  );
}

// ── DAX helpers ───────────────────────────────────────────────
function makePeriodFilter(period) {
  const { startDate, endDate } = getDateRange(period);
  return `'Transactions'[TXDATE] >= DATE(${startDate}) && 'Transactions'[TXDATE] <= DATE(${endDate})`;
}
function makeYtdFilter() {
  const _n = new Date();
  return `'Transactions'[TXDATE] >= DATE(${_n.getFullYear()},1,1) && 'Transactions'[TXDATE] <= DATE(${_n.getFullYear()},${_n.getMonth()+1},${_n.getDate()})`;
}

// ════════════════════════════════════════════════════════════
// ITEM SEARCH — find any product, see all branch on-hands
// ════════════════════════════════════════════════════════════
function ItemSearch({ onDrillProduct, period = 'MTD' }) {
  const isMobile = useIsMobile();
  const periodF = makePeriodFilter(period);
  const [query, setQuery] = React.useState('');
  const [submitted, setSubmitted] = React.useState('');

  const searchDax = submitted ? `EVALUATE
    TOPN(20,
      ADDCOLUMNS(
        FILTER('Stock',
          CONTAINSSTRING(LOWER('Stock'[Description]), LOWER("${submitted}")) ||
          CONTAINSSTRING(LOWER('Stock'[StockCode]),   LOWER("${submitted}"))
        ),
        "TotalOnHand", CALCULATE(SUM('StockBalances'[ONHAND])),
        "StockValue",  CALCULATE(SUM('StockBalances'[ONHAND])) * 'Stock'[AVRGCOST],
        "MTDSales",    CALCULATE(SUM('Transactions'[SALES]), ${periodF}),
        "BranchCount", CALCULATE(COUNTROWS('StockBalances'), 'StockBalances'[ONHAND]>0)
      ),
      [TotalOnHand], DESC
    )
    ORDER BY [TotalOnHand] DESC` : null;

  const { data, loading, error } = usePbi(searchDax);

  const doSearch = () => { if (query.trim().length > 1) setSubmitted(query.trim()); };
  const handleKey = e => { if (e.key === 'Enter') doSearch(); };

  return (
    <div style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:4, marginBottom:12 }}>
      <div style={{ padding:'14px 16px 12px', borderBottom:'1px solid var(--card-border)', display:'flex', alignItems:'center', gap:10 }}>
        <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--page-muted)', flexShrink:0 }}>Item Search</div>
        <div style={{ flex:1, display:'flex', gap:8 }}>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Search by name or code..."
            style={{ flex:1, padding:'7px 12px', background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:3, color:'var(--page-text)', fontFamily:'var(--font-body)', fontSize:12, outline:'none', minWidth:0 }}
          />
          <button onClick={doSearch} style={{ padding:'7px 16px', background:GOLD, border:'none', borderRadius:3, color:'var(--void)', fontFamily:'var(--font-mono)', fontSize:10, fontWeight:700, cursor:'pointer', flexShrink:0, minHeight:44 }}>SEARCH</button>
        </div>
      </div>
      {submitted && (
        loading ? <div style={{ padding:'12px 0' }}><Spinner/></div>
        : error ? <ErrorBox msg={error}/>
        : !data?.length
          ? <div style={{ padding:'20px', textAlign:'center', fontFamily:'var(--font-mono)', fontSize:11, color:'var(--page-muted)' }}>No results for "{submitted}"</div>
          : (data || []).map((item, i) => (
              <RowItem key={i} rank={i}
                label={item['Stock[Description]'] || item['Stock[StockCode]']}
                sub={`Code: ${item['Stock[StockCode]']} · ${fmtN(item['[BranchCount]'])} branches · MTD Sales: ${fmtR(item['[MTDSales]'])}`}
                value={fmtN(item['[TotalOnHand]']) + ' units'}
                value2={fmtR(item['[StockValue]'])}
                value2Color='var(--page-muted)'
                onClick={() => onDrillProduct(item['Stock[StockCode]'], item['Stock[Description]'])}/>
            ))
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// LEVEL 0 — Overview
// ════════════════════════════════════════════════════════════
function StockOverview({ onDrillDept, onDrillBranch, onDrillProduct, period = 'MTD' }) {
  const isMobile = useIsMobile();
  const periodF = makePeriodFilter(period);
  const ytdF    = makeYtdFilter();

  const kpiDax = `EVALUATE ROW(
    "TotalValue",   SUMX(FILTER('StockBalances','StockBalances'[ONHAND]>0), 'StockBalances'[ONHAND] * RELATED('Stock'[AVRGCOST])),
    "TotalLines",   COUNTROWS(FILTER('Stock', 'Stock'[ONHAND] > 0)),
    "ZeroLines",    COUNTROWS(FILTER('Stock', 'Stock'[ONHAND] <= 0)),
    "TotalUnits",   CALCULATE(SUM('StockBalances'[ONHAND]), 'StockBalances'[ONHAND] > 0),
    "YTDPurchases", CALCULATE(SUM('Transactions'[PURCHASES]), ${ytdF}),
    "YTDDays",      DATEDIFF(DATE(${new Date().getFullYear()},1,1), TODAY(), DAY)
  )`;

  const deptDax = `EVALUATE
    TOPN(15,
      ADDCOLUMNS(
        SUMMARIZE(FILTER('Stock', 'Stock'[ONHAND] > 0), 'Stock'[DEPARTMENT]),
        "DeptName",   MAXX(FILTER('Departments', 'Departments'[Dept] = EARLIER('Stock'[DEPARTMENT])), 'Departments'[DeptName]),
        "StockValue", CALCULATE(SUMX('StockBalances','StockBalances'[ONHAND]*RELATED('Stock'[AVRGCOST]))),
        "Units",      CALCULATE(SUM('StockBalances'[ONHAND])),
        "Lines",      CALCULATE(COUNTROWS('Stock'), 'Stock'[ONHAND] > 0),
        "MTDSales",   CALCULATE(SUM('Transactions'[SALES]), ${periodF})
      ),
      [StockValue], DESC
    )
    ORDER BY [StockValue] DESC`;

  const branchDax = `EVALUATE
    TOPN(31,
      ADDCOLUMNS(
        SUMMARIZE(FILTER('Transactions', ${ytdF}), 'CompanyList'[COMPANY NAME]),
        "MTDSales",    CALCULATE(SUM('Transactions'[SALES]),      ${periodF}),
        "MTDUnits",    CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${periodF}),
        "MTDPurchases",CALCULATE(SUM('Transactions'[PURCHASES]),  ${periodF})
      ),
      [MTDSales], DESC
    )
    ORDER BY [MTDSales] DESC`;

  const slowDax = `EVALUATE
    TOPN(10,
      FILTER(
        ADDCOLUMNS(
          FILTER('Stock', 'Stock'[ONHAND] > 0),
          "SBOnHand",  CALCULATE(SUM('StockBalances'[ONHAND])),
          "StockValue",CALCULATE(SUM('StockBalances'[ONHAND])) * 'Stock'[AVRGCOST],
          "MTDSales",  CALCULATE(SUM('Transactions'[SALES]), ${periodF})
        ),
        [StockValue] > 10000 && ([MTDSales] = 0 || ISBLANK([MTDSales]))
      ),
      [StockValue], DESC
    )`;

  const { data: kpiRaw, loading: l0, error: e0, reload: r0 } = usePbi(kpiDax);
  const { data: depts,  loading: l1, error: e1, reload: r1 } = usePbi(deptDax);
  const { data: branches, loading: l2, error: e2, reload: r2 } = usePbi(branchDax);
  const { data: slow,   loading: l3, error: e3, reload: r3 } = usePbi(slowDax);

  const kpi = Array.isArray(kpiRaw) ? kpiRaw[0] : kpiRaw;
  const totalValue  = kpi?.['[TotalValue]']   || 0;
  const totalLines  = kpi?.['[TotalLines]']   || 0;
  const zeroLines   = kpi?.['[ZeroLines]']    || 0;
  const totalUnits  = kpi?.['[TotalUnits]']   || 0;
  const ytdPurch    = kpi?.['[YTDPurchases]'] || 0;
  const ytdDays     = kpi?.['[YTDDays]']      || 1;
  const annualPurch = ytdPurch / ytdDays * 365;
  const daysOnHand  = totalValue > 0 && annualPurch > 0 ? Math.round(totalValue / (annualPurch / 365)) : null;

  const maxDeptVal  = Math.max(...(depts || []).map(d => d['[StockValue]'] || 0));
  const maxBranchS  = Math.max(...(branches || []).map(b => b['[MTDSales]'] || 0));

  const kpis2col = isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)';

  return (
    <div style={{ minWidth:0, overflowX:'hidden' }}>
      {/* Page header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.18em', textTransform:'uppercase', color:'var(--page-muted)' }}>Stock & Inventory</div>
        <div style={{ fontFamily:'var(--font-mono)', fontSize:9, padding:'3px 10px', background:'rgba(26,173,99,0.1)', color:GREEN, borderRadius:2 }}>LIVE</div>
      </div>

      {/* KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:kpis2col, gap:10, marginBottom:16 }}>
        {l0 ? [0,1,2,3].map(i => <div key={i} style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:4, height:88 }}><Spinner/></div>)
          : e0 ? <div style={{ gridColumn:'1/-1' }}><ErrorBox msg={e0} onRetry={r0}/></div>
          : <>
            <KpiCard label="Total Stock Value" value={fmtR(totalValue)} sub={`${fmtN(totalLines)} active lines`}/>
            <KpiCard label="Days on Hand" value={daysOnHand != null ? `${daysOnHand}d` : '—'}
              sub={daysOnHand > 60 ? 'Overstocked — review' : daysOnHand < 15 ? 'Low — risk of stockout' : 'Healthy range'}
              subColor={daysOnHand > 60 ? RED : daysOnHand < 15 ? AMBER : GREEN}
              alert={daysOnHand > 60}/>
            <KpiCard label="Total Units on Hand" value={fmtN(totalUnits)} sub="across all branches"/>
            <KpiCard label="Zero Stock Lines" value={fmtN(zeroLines)}
              sub="items currently out of stock"
              subColor={zeroLines > 50 ? RED : AMBER}
              alert={zeroLines > 100}/>
          </>
        }
      </div>

      {/* Dept + Branch */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) minmax(0,1fr)', gap:12, marginBottom:12 }}>

        {/* By Department */}
        <Card>
          <CardHeader title="Stock Value by Department" subtitle="Click to drill down"/>
          {l1 ? <Spinner/> : e1 ? <ErrorBox msg={e1} onRetry={r1}/>
            : (depts || []).map((d, i) => (
              <RowItem key={i} rank={i}
                label={d['[DeptName]'] || d['Stock[DEPARTMENT]'] || '(No Dept)'}
                sub={`${fmtN(d['[Lines]'])} lines · ${fmtN(d['[Units]'])} units`}
                value={fmtR(d['[StockValue]'])}
                value2={d['[MTDSales]'] ? `MTD Sales: ${fmtR(d['[MTDSales]'])}` : 'No MTD sales'}
                value2Color={d['[MTDSales]'] > 0 ? GREEN : AMBER}
                bar={d['[StockValue]']} barMax={maxDeptVal}
                onClick={() => onDrillDept(d['Stock[DEPARTMENT]'], d['[DeptName]'])}/>
            ))
          }
        </Card>

        {/* By Branch */}
        <Card>
          <CardHeader title="Sales Activity by Branch" subtitle="Click to drill down"/>
          {l2 ? <Spinner/> : e2 ? <ErrorBox msg={e2} onRetry={r2}/>
            : (branches || []).map((b, i) => {
              const margin = b['[MTDSales]'] && b['[MTDPurchases]']
                ? (b['[MTDSales]'] - b['[MTDPurchases]']) / b['[MTDSales]']
                : null;
              return (
                <RowItem key={i} rank={i}
                  label={b['CompanyList[COMPANY NAME]']}
                  sub={`${fmtN(b['[MTDUnits]'])} units · Margin: ${margin != null ? (margin*100).toFixed(1)+'%' : '—'}`}
                  value={fmtR(b['[MTDSales]'])}
                  bar={b['[MTDSales]']} barMax={maxBranchS}
                  onClick={() => onDrillBranch(b['CompanyList[COMPANY NAME]'])}/>
              );
            })
          }
        </Card>
      </div>

      {/* Item Search */}
      <ItemSearch onDrillProduct={onDrillProduct} period={period}/>

      {/* Slow movers */}
      <Card>
        <CardHeader title="Slow Movers — High Value, No MTD Sales" subtitle="Stock tying up cash with zero movement this month"/>
        {l3 ? <Spinner/> : e3 ? <ErrorBox msg={e3} onRetry={r3}/>
          : (slow || []).length === 0
            ? <div style={{ padding:'24px', textAlign:'center', fontFamily:'var(--font-mono)', fontSize:11, color:GREEN }}>No slow movers detected — good stock velocity</div>
            : (slow || []).map((s, i) => (
              <RowItem key={i} rank={i}
                label={s['Stock[Description]'] || s['Stock[StockCode]']}
                sub={`Code: ${s['Stock[StockCode]']} · On Hand: ${fmtN(s['[SBOnHand]'])} units`}
                value={fmtR(s['[StockValue]'])}
                value2="No MTD sales" value2Color={RED}
                onClick={() => onDrillProduct(s['Stock[StockCode]'], s['Stock[Description]'])}/>
            ))
        }
      </Card>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// LEVEL 1a — Department Detail
// ════════════════════════════════════════════════════════════
function StockDeptDetail({ dept, deptName, onDrillProduct, period = 'MTD' }) {
  const isMobile = useIsMobile();
  const periodF = makePeriodFilter(period);
  const ytdF    = makeYtdFilter();
  const deptFilter = `'Stock'[DEPARTMENT] = "${dept}"`;

  const kpiDax = `EVALUATE ROW(
    "TotalValue", CALCULATE(SUMX('StockBalances','StockBalances'[ONHAND]*RELATED('Stock'[AVRGCOST])), FILTER('Stock',${deptFilter})),
    "TotalLines", CALCULATE(COUNTROWS('Stock'), ${deptFilter}, 'Stock'[ONHAND] > 0),
    "TotalUnits", CALCULATE(SUM('Stock'[ONHAND]), ${deptFilter}, 'Stock'[ONHAND] > 0),
    "MTDSales",   CALCULATE(SUM('Transactions'[SALES]), ${periodF})
  )`;

  const productsDax = `EVALUATE
    TOPN(50,
      ADDCOLUMNS(
        FILTER('Stock', ${deptFilter}),
        "TotalOnHand", CALCULATE(SUM('StockBalances'[ONHAND])),
        "StockValue",  CALCULATE(SUM('StockBalances'[ONHAND])) * 'Stock'[AVRGCOST],
        "MTDSales",    CALCULATE(SUM('Transactions'[SALES]),      ${periodF}),
        "MTDUnits",    CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${periodF})
      ),
      [StockValue], DESC
    )
    ORDER BY [StockValue] DESC`;

  const branchDax = `EVALUATE
    TOPN(31,
      ADDCOLUMNS(
        SUMMARIZE(
          FILTER('Transactions', ${periodF}),
          'CompanyList'[COMPANY NAME]
        ),
        "MTDSales",  CALCULATE(SUM('Transactions'[SALES]),      ${periodF}),
        "MTDUnits",  CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${periodF})
      ),
      [MTDSales], DESC
    )
    ORDER BY [MTDSales] DESC`;

  const { data: kpiRaw, loading: l0, error: e0, reload: r0 } = usePbi(kpiDax);
  const { data: products, loading: l1, error: e1, reload: r1 } = usePbi(productsDax);
  const { data: branches, loading: l2, error: e2, reload: r2 } = usePbi(branchDax);

  const kpi = Array.isArray(kpiRaw) ? kpiRaw[0] : kpiRaw;
  const maxVal = Math.max(...(products || []).map(p => p['[StockValue]'] || 0));

  return (
    <div style={{ minWidth:0, overflowX:'hidden' }}>
      {/* Dept KPIs */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap:10, marginBottom:16 }}>
        {l0 ? [0,1,2].map(i => <div key={i} style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:4, height:80 }}><Spinner/></div>)
          : e0 ? <div style={{ gridColumn:'1/-1' }}><ErrorBox msg={e0} onRetry={r0}/></div>
          : <>
            <KpiCard label="Stock Value" value={fmtR(kpi?.['[TotalValue]'])} sub={`${fmtN(kpi?.['[TotalLines]'])} lines`}/>
            <KpiCard label="Units on Hand" value={fmtN(kpi?.['[TotalUnits]'])}/>
            <KpiCard label="MTD Sales" value={fmtR(kpi?.['[MTDSales]'])} subColor={GREEN}/>
          </>
        }
      </div>

      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1.4fr) minmax(0,1fr)', gap:12 }}>
        {/* Products */}
        <Card>
          <CardHeader title={`Products — ${deptName || dept}`} subtitle="Ranked by stock value · Click to drill down"/>
          {l1 ? <Spinner/> : e1 ? <ErrorBox msg={e1} onRetry={r1}/>
            : (products || []).map((p, i) => (
              <RowItem key={i} rank={i}
                label={p['Stock[Description]'] || p['Stock[StockCode]']}
                sub={`${fmtN(p['[TotalOnHand]'])} units · Cost: ${fmtR(p['Stock[AVRGCOST]'])}`}
                value={fmtR(p['[StockValue]'])}
                value2={p['[MTDSales]'] > 0 ? `MTD: ${fmtR(p['[MTDSales]'])}` : 'No MTD sales'}
                value2Color={p['[MTDSales]'] > 0 ? GREEN : AMBER}
                bar={p['[StockValue]']} barMax={maxVal}
                onClick={() => onDrillProduct(p['Stock[StockCode]'], p['Stock[Description]'])}/>
            ))
          }
        </Card>

        {/* Branch sales for this dept */}
        <Card>
          <CardHeader title="Sales by Branch — MTD"/>
          {l2 ? <Spinner/> : e2 ? <ErrorBox msg={e2} onRetry={r2}/>
            : (branches || []).filter(b => b['[MTDSales]'] > 0).map((b, i) => (
              <RowItem key={i} rank={i}
                label={b['CompanyList[COMPANY NAME]']}
                sub={`${fmtN(b['[MTDUnits]'])} units`}
                value={fmtR(b['[MTDSales]'])}/>
            ))
          }
        </Card>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// LEVEL 1b — Branch/Store Detail
// ════════════════════════════════════════════════════════════
function StockBranchDetail({ branch, onDrillProduct, period = 'MTD' }) {
  const isMobile = useIsMobile();
  const periodF = makePeriodFilter(period);
  const ytdF    = makeYtdFilter();
  const kpiDax = `EVALUATE ROW(
    "MTDSales",     CALCULATE(SUM('Transactions'[SALES]),      ${periodF}, 'CompanyList'[COMPANY NAME]="${branch}"),
    "MTDUnits",     CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${periodF}, 'CompanyList'[COMPANY NAME]="${branch}"),
    "MTDPurchases", CALCULATE(SUM('Transactions'[PURCHASES]),  ${periodF}, 'CompanyList'[COMPANY NAME]="${branch}")
  )`;

  // Top products stocked & sold at this branch
  const topProductsDax = `EVALUATE
    TOPN(30,
      CALCULATETABLE(
        ADDCOLUMNS(
          SUMMARIZE('Transactions', 'Transactions'[CODE], 'Stock'[Description]),
          "MTDSales",   CALCULATE(SUM('Transactions'[SALES]),      ${periodF}),
          "MTDUnits",   CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${periodF}),
          "BranchQty",  CALCULATE(SUM('StockBalances'[ONHAND])),
          "BranchVal",   CALCULATE(SUM('StockBalances'[ONHAND])) * MAX('Stock'[AVRGCOST])
        ),
        ${periodF},
        FILTER('CompanyList', 'CompanyList'[COMPANY NAME] = "${branch}")
      ),
      [MTDSales], DESC
    )
    ORDER BY [MTDSales] DESC`;

  // Dept breakdown for this branch
  const deptDax = `EVALUATE
    TOPN(15,
      CALCULATETABLE(
        ADDCOLUMNS(
          SUMMARIZE('Transactions', 'Departments'[DeptName]),
          "MTDSales",    CALCULATE(SUM('Transactions'[SALES]),      ${periodF}),
          "MTDUnits",    CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${periodF}),
          "MTDPurchases",CALCULATE(SUM('Transactions'[PURCHASES]),  ${periodF})
        ),
        ${periodF},
        FILTER('CompanyList', 'CompanyList'[COMPANY NAME] = "${branch}")
      ),
      [MTDSales], DESC
    )
    ORDER BY [MTDSales] DESC`;

  const { data: kpiRaw, loading: l0, error: e0, reload: r0 } = usePbi(kpiDax);
  const { data: products, loading: l1, error: e1, reload: r1 } = usePbi(topProductsDax);
  const { data: depts,    loading: l2, error: e2, reload: r2 } = usePbi(deptDax);

  const kpi = Array.isArray(kpiRaw) ? kpiRaw[0] : kpiRaw;
  const margin = kpi?.['[MTDSales]'] && kpi?.['[MTDPurchases]']
    ? (kpi['[MTDSales]'] - kpi['[MTDPurchases]']) / kpi['[MTDSales]']
    : null;
  const maxSales = Math.max(...(products || []).map(p => p['[MTDSales]'] || 0));
  const maxDept  = Math.max(...(depts || []).map(d => d['[MTDSales]'] || 0));

  return (
    <div style={{ minWidth:0, overflowX:'hidden' }}>
      {/* Branch KPIs */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap:10, marginBottom:16 }}>
        {l0 ? [0,1,2].map(i => <div key={i} style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:4, height:80 }}><Spinner/></div>)
          : e0 ? <div style={{ gridColumn:'1/-1' }}><ErrorBox msg={e0} onRetry={r0}/></div>
          : <>
            <KpiCard label="MTD Revenue" value={fmtR(kpi?.['[MTDSales]'])} subColor={GREEN}/>
            <KpiCard label="MTD Units Sold" value={fmtN(kpi?.['[MTDUnits]'])}/>
            <KpiCard label="Gross Margin" value={margin != null ? `${(margin*100).toFixed(1)}%` : '—'} subColor={GREEN}/>
          </>
        }
      </div>

      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1.4fr) minmax(0,1fr)', gap:12 }}>
        {/* Top products at this branch */}
        <Card>
          <CardHeader title={`Top Products — ${branch}`} subtitle="Click product to see full detail"/>
          {l1 ? <Spinner/> : e1 ? <ErrorBox msg={e1} onRetry={r1}/>
            : (products || []).map((p, i) => (
              <RowItem key={i} rank={i}
                label={p['Stock[Description]'] || p['Transactions[CODE]']}
                sub={`${fmtN(p['[MTDUnits]'])} units · On hand: ${fmtN(p['[BranchQty]'])}`}
                value={fmtR(p['[MTDSales]'])}
                value2={p['[BranchVal]'] > 0 ? `Stock: ${fmtR(p['[BranchVal]'])}` : null}
                value2Color='var(--page-muted)'
                bar={p['[MTDSales]']} barMax={maxSales}
                onClick={() => onDrillProduct(p['Transactions[CODE]'], p['Stock[Description]'])}/>
            ))
          }
        </Card>

        {/* Dept breakdown */}
        <Card>
          <CardHeader title="Department Breakdown — MTD"/>
          {l2 ? <Spinner/> : e2 ? <ErrorBox msg={e2} onRetry={r2}/>
            : (depts || []).map((d, i) => {
              const m = d['[MTDSales]'] && d['[MTDPurchases]']
                ? (d['[MTDSales]'] - d['[MTDPurchases]']) / d['[MTDSales]']
                : null;
              return (
                <RowItem key={i} rank={i}
                  label={d['Departments[DeptName]']}
                  sub={`${fmtN(d['[MTDUnits]'])} units`}
                  value={fmtR(d['[MTDSales]'])}
                  value2={m != null ? `Margin: ${(m*100).toFixed(1)}%` : null}
                  value2Color={m > 0.3 ? GREEN : AMBER}
                  bar={d['[MTDSales]']} barMax={maxDept}/>
              );
            })
          }
        </Card>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// LEVEL 2 — Product Detail
// ════════════════════════════════════════════════════════════
function StockProductDetail({ code, name, period = 'MTD' }) {
  const isMobile = useIsMobile();
  const periodF = makePeriodFilter(period);
  const ytdF    = makeYtdFilter();
  const codeF = `'Stock'[StockCode] = "${code}"`;
  const txCodeF = `'Transactions'[CODE] = "${code}"`;

  const stockDax = `EVALUATE
    ADDCOLUMNS(
      SELECTCOLUMNS(
        FILTER('Stock', ${codeF}),
        "Code",        'Stock'[StockCode],
        "Description", 'Stock'[Description],
        "SellPrice",   'Stock'[SELLPRICE1],
        "AvgCost",     'Stock'[AVRGCOST],
        "Department",  'Stock'[DEPARTMENT],
      "DeptName",    RELATED('Departments'[DeptName])
      ),
      "TotalQty",    CALCULATE(SUM('StockBalances'[ONHAND]),  FILTER('StockBalances','StockBalances'[CODE]="${code}")),
      "TotalLaybye", CALCULATE(SUM('StockBalances'[LAYBYE]),  FILTER('StockBalances','StockBalances'[CODE]="${code}")),
      "StockVal",    CALCULATE(SUMX(FILTER('StockBalances','StockBalances'[CODE]="${code}"&&'StockBalances'[ONHAND]>0),'StockBalances'[ONHAND]*RELATED('Stock'[AVRGCOST])))
    )`;

  // Stock on hand per branch from StockBalances — RELATED scopes CONO automatically
  const branchStockDax = `EVALUATE
    TOPN(31,
      ADDCOLUMNS(
        FILTER('StockBalances', 'StockBalances'[CODE] = "${code}"),
        "BranchName", RELATED('CompanyList'[COMPANY NAME]),
        "BranchLaybye", 'StockBalances'[LAYBYE],
        "YTDUnits",   CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${txCodeF}, ${ytdF}),
        "MTDUnits",   CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${txCodeF}, ${periodF}),
        "MTDSales",   CALCULATE(SUM('Transactions'[SALES]),      ${txCodeF}, ${periodF}),
        "YTDSales",   CALCULATE(SUM('Transactions'[SALES]),      ${txCodeF}, ${ytdF})
      ),
      [YTDSales], DESC
    )
    ORDER BY [YTDSales] DESC`;

  const trendDax = `EVALUATE
    ADDCOLUMNS(
      FILTER(
        SUMMARIZE(
          FILTER('Transactions', ${txCodeF}),
          'Date'[YYYYMM], 'Date'[MonthName], 'Date'[Year]
        ),
        'Date'[YYYYMM] >= 202404
      ),
      "Revenue", CALCULATE(SUM('Transactions'[SALES]),      ${txCodeF}),
      "Units",   CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${txCodeF})
    )
    ORDER BY 'Date'[YYYYMM] ASC`;

  const { data: stockRaw, loading: l0, error: e0, reload: r0 } = usePbi(stockDax);
  const { data: branches, loading: l1, error: e1, reload: r1 } = usePbi(branchStockDax);
  const { data: trend,    loading: l2, error: e2, reload: r2 } = usePbi(trendDax);

  const stock = Array.isArray(stockRaw) ? stockRaw[0] : stockRaw;
  const onHand   = stock?.['[TotalQty]']    || 0;
  const laybye   = stock?.['[TotalLaybye]']    || 0;
  const avgCost  = stock?.['[AvgCost]']   || 0;
  const sellPrice= stock?.['[SellPrice]'] || 0;
  const stockVal = stock?.['[StockVal]']  || (onHand * avgCost);
  const margin   = sellPrice > 0 && avgCost > 0 ? (sellPrice - avgCost) / sellPrice : null;

  const trendData = (trend || [])
    .filter(r => r['[Revenue]'] != null)
    .map(r => ({
      label: `${r['Date[MonthName]']?.slice(0,3)} ${String(r['Date[Year]']).slice(2)}`,
      Revenue: r['[Revenue]'],
      Units: r['[Units]'],
    }));

  const tip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background:'var(--void)', border:'1px solid var(--card-border)', padding:'10px 14px', borderRadius:3 }}>
        <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)', marginBottom:4 }}>{label}</div>
        {payload.map((p,i) => <div key={i} style={{ fontSize:12, color:p.color }}>{p.name}: {p.name==='Revenue' ? fmtR(p.value) : fmtN(p.value)}</div>)}
      </div>
    );
  };

  return (
    <div style={{ minWidth:0, overflowX:'hidden' }}>
      {/* Product header card */}
      <div style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:4, padding: isMobile ? '14px 12px' : '18px 16px', marginBottom:12 }}>
        <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--page-muted)', marginBottom:6 }}>{code}</div>
        <div style={{ fontFamily:'var(--font-display)', fontSize: isMobile ? 16 : 18, fontWeight:700, color:'var(--page-text)', marginBottom:12, wordBreak:'break-word' }}>{name || code}</div>
        {l0 ? <Spinner/> : e0 ? <ErrorBox msg={e0} onRetry={r0}/> : stock && (
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(6,1fr)', gap: isMobile ? 10 : 12 }}>
            {[
              ['On Hand',     fmtN(onHand)],
              ['On Laybye',   fmtN(laybye)],
              ['Stock Value', fmtR(stockVal)],
              ['Sell Price',  fmtR(sellPrice)],
              ['Avg Cost',    fmtR(avgCost)],
              ['Margin',      margin != null ? `${(margin*100).toFixed(1)}%` : '—'],
            ].map(([l,v]) => (
              <div key={l}>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:4 }}>{l}</div>
                <div style={{ fontFamily:'var(--font-display)', fontSize:16, fontWeight:600, color:'var(--page-text)' }}>{v}</div>
              </div>
            ))}
          </div>
        )}
        <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)', marginTop:10 }}>
          Dept: {stock?.['[DeptName]'] || stock?.['[Department]'] || '—'}
        </div>
      </div>

      {/* Trend + Branch breakdown */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1.4fr) minmax(0,1fr)', gap:12 }}>
        {/* Monthly trend */}
        <Card>
          <CardHeader title="Monthly Sales Trend — All Time"/>
          <div style={{ padding:'0 16px 16px' }}>
            {l2 ? <Spinner/> : e2 ? <ErrorBox msg={e2} onRetry={r2}/>
              : trendData.length === 0
                ? <div style={{ padding:24, textAlign:'center', color:'var(--page-muted)', fontFamily:'var(--font-mono)', fontSize:11 }}>No sales history</div>
                : <>
                  <div style={{ display:'flex', gap:14, marginBottom:10, marginTop:8 }}>
                    {[['Revenue', GOLD],['Units','#4a9eff']].map(([k,c]) => (
                      <div key={k} style={{ display:'flex', alignItems:'center', gap:5 }}>
                        <div style={{ width:8, height:8, borderRadius:'50%', background:c }}/>
                        <span style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)' }}>{k}</span>
                      </div>
                    ))}
                  </div>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={trendData} barGap={2}>
                      <XAxis dataKey="label" tick={{ fontFamily:'var(--font-mono)', fontSize:9, fill:'var(--page-muted)' }} axisLine={false} tickLine={false}/>
                      <YAxis hide/>
                      <Tooltip content={tip}/>
                      <Bar dataKey="Revenue" fill={GOLD} radius={[2,2,0,0]} name="Revenue"/>
                    </BarChart>
                  </ResponsiveContainer>
                </>
            }
          </div>
        </Card>

        {/* Stock & sales per branch */}
        <Card>
          <CardHeader title="Sales by Branch" subtitle="YTD units sold per store"/>
          {l1 ? <Spinner/> : e1 ? <ErrorBox msg={e1} onRetry={r1}/>
            : (branches || []).filter(b => b['StockBalances[ONHAND]'] > 0 || b['[YTDUnits]'] > 0).map((b, i) => (
              <RowItem key={i} rank={i}
                label={b['[BranchName]'] || b['CompanyList[COMPANY NAME]']}
                sub={`On Hand: ${fmtN(b['StockBalances[ONHAND]'])} · Laybye: ${fmtN(b['[BranchLaybye]'])} · YTD: ${fmtN(b['[YTDUnits]'])} units`}
                value={fmtR(b['[YTDSales]'])}
                value2={b['[MTDSales]'] > 0 ? `MTD: ${fmtR(b['[MTDSales]'])}` : 'No MTD sales'}
                value2Color={b['[MTDSales]'] > 0 ? GREEN : 'var(--page-muted)'}/>
            ))
          }
        </Card>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// MAIN — Stock Page (manages navigation state)
// ════════════════════════════════════════════════════════════
export default function StockPage() {
  const { period = 'MTD' } = useOutletContext() || {};
  const isMobile = useIsMobile();
  const [nav, setNav] = React.useState([{ level:0, label:'Stock & Inventory' }]);

  const current = nav[nav.length - 1];
  const navigateTo  = crumb => setNav(prev => [...prev, crumb]);
  const navigateBack= idx   => setNav(prev => prev.slice(0, idx + 1));
  const crumbs = nav.map(n => ({ label: n.label }));

  return (
    <div style={{ animation:'fadeUp 0.4s ease both', minWidth:0, overflowX:'hidden' }}>
      <Breadcrumb crumbs={crumbs} onNavigate={navigateBack}/>

      {current.level === 0 && (
        <StockOverview
          key={period}
          period={period}
          onDrillDept={(dept, deptName) => navigateTo({ level:1, label: deptName || dept, dept, deptName })}
          onDrillBranch={branch => navigateTo({ level:2, label: branch, branch })}
          onDrillProduct={(code, name) => navigateTo({ level:3, label: name || code, code, name })}
        />
      )}
      {current.level === 1 && (
        <StockDeptDetail
          key={current.dept + period}
          dept={current.dept}
          deptName={current.deptName}
          period={period}
          onDrillProduct={(code, name) => navigateTo({ level:3, label: name || code, code, name })}
        />
      )}
      {current.level === 2 && (
        <StockBranchDetail
          key={current.branch + period}
          branch={current.branch}
          period={period}
          onDrillProduct={(code, name) => navigateTo({ level:3, label: name || code, code, name })}
        />
      )}
      {current.level === 3 && (
        <StockProductDetail
          key={current.code + period}
          code={current.code}
          name={current.name}
          period={period}
        />
      )}
    </div>
  );
}
